import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Icon from '@vtblife/uikit-icons';
import { useTransformations, useTextareaHandlers, useContent, useInputId, useInputStatus } from '../input/utils';
import { useGroup } from '../group/utils';
import Label from '../input/label';
import { useAutoResizable } from './hooks/use-auto-resizable';
import normalizeCSSModules from '../../utils/css-modules';
import styles from '../input/input.module.css';
import textareaStyles from './textarea.module.css';
const s = normalizeCSSModules(styles);
const sTextarea = normalizeCSSModules(textareaStyles);
const Loader = ({ fade }) => (React.createElement("svg", { className: classNames(fade && s.fade), width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M11 3a1 1 0 011-1 10 10 0 0110 10 1 1 0 11-2 0 7.999 7.999 0 00-8-8 1 1 0 01-1-1z", fill: "currentColor" })));
export const Textarea = (props) => {
    const { autoComplete = 'off', autoFocus = false, background = 'primary', children, dataTest, disabled = false, error = false, floating = true, formatter, gtm, hint, id: externalId = true, info = null, inputMode = 'text', label = '', loading = false, loaderFade = false, maxLength, name, onChange = () => { }, parser, placeholder = '', readOnly = false, required, size: externalSize = 's', spaced = false, valid = false, value: externalValue, warning = false, passRequiredToHTMLInput = true, onClick, inputRef, wrapperRef, rows = 3, resize = 'vertical', autoHeight = false, maxRows = 6, } = props;
    let { autofocus = false, readonly = false, success = false } = props;
    const { format, parse } = useTransformations(formatter, parser);
    const handlers = useTextareaHandlers(props);
    const input = useRef(null);
    const container = useRef(null);
    const counterRef = useRef(null);
    const [value, setValue] = useState(format(externalValue));
    const autoRows = useAutoResizable({
        value,
        initRows: rows,
        textArea: input,
        counterRef,
        autoHeight,
        maxRows,
    });
    const id = useInputId(externalId);
    const group = useGroup();
    const size = (group.present && group.size) || externalSize;
    autofocus = autofocus || autoFocus;
    readonly = readonly || readOnly;
    const handleClick = useCallback(event => {
        var _a;
        if (event.target === container.current) {
            (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [onClick]);
    const handleValueChange = useCallback((newValue, event) => {
        const text = format(newValue);
        if (value !== text) {
            onChange(parse(newValue), event);
        }
        setValue(text);
    }, [format, parse, onChange, value]);
    const handleInputOnChange = useCallback(event => handleValueChange(event.target.value, event), [handleValueChange]);
    useEffect(() => {
        setValue(format(externalValue));
    }, [externalValue, format]);
    success = success || valid;
    const status = useInputStatus(success, error, warning);
    const content = useContent(children);
    const assignInputRef = useCallback(el => {
        input.current = el;
        if (inputRef) {
            inputRef.current = el;
        }
    }, [inputRef]);
    const attrs = Object.assign({}, (passRequiredToHTMLInput && { required }));
    return (React.createElement("div", { className: classNames(s.wrapper, spaced && s.spaced, group.present && !group.first && s.group), ref: wrapperRef, style: Object.assign(Object.assign({}, (group.present && { flex: group.width })), (group.present && { maxWidth: group.maxWidth })) },
        React.createElement("span", { className: classNames(s.container, s[size], sTextarea.container, sTextarea[size], warning && s.warning, error && s.error, success && s.success, !label && size === 'l' && s.single, !label && size === 'l' && sTextarea.single, label && size !== 'l' && s.labeled, floating && size === 'l' && s.floating, disabled && s.disabled), ref: container, "data-gtm": gtm, "data-test": dataTest, onClick: handleClick },
            content.start,
            React.createElement("textarea", Object.assign({ autoComplete: autoComplete, autoCorrect: "off", autoFocus: autofocus, className: classNames(s.control, sTextarea[size], sTextarea.control, sTextarea[resize], !label && size === 'l' && sTextarea.controlSingle, value.length === 0 && s.empty), disabled: disabled, id: id, inputMode: inputMode, maxLength: maxLength, name: name, placeholder: placeholder, readOnly: readonly, value: value, ref: assignInputRef, onChange: handleInputOnChange, rows: autoRows }, attrs, handlers)),
            autoHeight && (React.createElement("textarea", { "aria-hidden": true, tabIndex: -1, readOnly: true, ref: counterRef, className: textareaStyles.shadow })),
            React.createElement("span", { className: classNames(sTextarea.containerRigth, sTextarea[size]) },
                (status.icon || loading) && (React.createElement("span", { "data-addon-end": true, className: classNames(s.end, loading ? s.loader : s.status), ref: status.ref }, loading ? React.createElement(Loader, { fade: loaderFade }) : React.createElement(Icon, { name: status.icon }))),
                content.end ||
                    (info && (React.createElement("span", { "data-addon-end": true, className: classNames(s.end, s.icon), ref: info },
                        React.createElement(Icon, { name: "information-circle" }))))),
            label && (React.createElement(Label, { container: container, size: size, content: content, info: info, reset: false, postfix: "", id: id, required: required, status: status }, label)),
            React.createElement("span", { className: classNames(s.bg, s[background], (!group.present || (group.present && group.first)) && s.roundedLeft, (!group.present || (group.present && group.last)) && s.roundedRight) })),
        hint && (React.createElement("span", { className: classNames(s.hint, error && s.error, warning && s.warning), "data-test": error ? 'error' : warning ? 'warning' : 'hint' }, hint))));
};
//# sourceMappingURL=textarea.js.map